import { useState } from 'react';
import type {
  AsaasAccountStatusSchema,
  AsaasEnvironmentSchema,
  CreateAsaasAccountInput,
} from '@control-center/shared';

import { Field, Input, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

export interface AsaasAccountFormValues {
  name: string;
  environment: AsaasEnvironmentSchema;
  webhook_path: string;
  is_default: boolean;
  status: AsaasAccountStatusSchema;
  api_key: string;
  webhook_secret: string;
}

interface AsaasAccountFormProps {
  mode: 'create' | 'edit';
  initial?: Partial<AsaasAccountFormValues>;
  apiKeyRequired: boolean;
  webhookSecretRequired: boolean;
  isPending: boolean;
  onSubmit: (values: CreateAsaasAccountInput) => void | Promise<void>;
  onCancel: () => void;
}

const ENV_OPTIONS: { value: AsaasEnvironmentSchema; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'sandbox', label: 'Sandbox' },
];

const STATUS_OPTIONS: { value: AsaasAccountStatusSchema; label: string }[] = [
  { value: 'active', label: 'Ativa' },
  { value: 'paused', label: 'Pausada' },
  { value: 'disabled', label: 'Desativada' },
];

export function AsaasAccountForm({
  mode,
  initial,
  apiKeyRequired,
  webhookSecretRequired,
  isPending,
  onSubmit,
  onCancel,
}: AsaasAccountFormProps): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  const [environment, setEnvironment] = useState<AsaasEnvironmentSchema>(
    initial?.environment ?? 'production',
  );
  const [webhookPath, setWebhookPath] = useState(initial?.webhook_path ?? '');
  const [isDefault, setIsDefault] = useState(initial?.is_default ?? false);
  const [status, setStatus] = useState<AsaasAccountStatusSchema>(initial?.status ?? 'active');
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const nameValid = name.trim().length > 0;
  const pathValid = /^[a-z0-9-]+$/.test(webhookPath);
  const apiKeyValid = apiKeyRequired ? apiKey.length >= 10 : apiKey === '' || apiKey.length >= 10;
  const secretValid = webhookSecretRequired
    ? webhookSecret.length >= 8
    : webhookSecret === '' || webhookSecret.length >= 8;

  const canSubmit = nameValid && pathValid && apiKeyValid && secretValid && !isPending;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    const payload: CreateAsaasAccountInput = {
      name: name.trim(),
      environment,
      webhook_path: webhookPath.trim(),
      is_default: isDefault,
      status,
      api_key: apiKey,
      webhook_secret: webhookSecret,
    };
    // Em edit, só envia secrets se preenchidos (rotação opcional)
    if (mode === 'edit' && apiKey === '') {
      delete (payload as Partial<CreateAsaasAccountInput>).api_key;
    }
    if (mode === 'edit' && webhookSecret === '') {
      delete (payload as Partial<CreateAsaasAccountInput>).webhook_secret;
    }
    await onSubmit(payload);
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="Nome"
          required
          error={name.length > 0 && !nameValid ? 'Nome obrigatório.' : undefined}
        >
          {({ inputId }) => (
            <Input
              id={inputId}
              type="text"
              placeholder="Opus Owl Master"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Ambiente" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as AsaasEnvironmentSchema)}
            >
              {ENV_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Webhook path"
          required
          hint="lowercase + dígitos + hífen (ex: owl-master)"
          error={webhookPath.length > 0 && !pathValid ? 'Formato inválido.' : undefined}
        >
          {({ inputId }) => (
            <Input
              id={inputId}
              type="text"
              placeholder="owl-master"
              value={webhookPath}
              onChange={(e) => setWebhookPath(e.target.value)}
            />
          )}
        </Field>
        <Field label="Status" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={status}
              onChange={(e) => setStatus(e.target.value as AsaasAccountStatusSchema)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <span>Conta default (cobranças sem account explícita usam essa)</span>
      </label>

      <fieldset className="border-t border-line pt-4">
        <legend className="mb-3 text-[12px] font-medium uppercase tracking-wider text-muted-3">
          Credenciais
        </legend>
        <div className="grid grid-cols-1 gap-4">
          <Field
            label="API key"
            required={apiKeyRequired}
            hint={
              mode === 'edit'
                ? 'Deixe vazio pra manter a atual. Preencha pra rotacionar.'
                : 'Cole a chave do painel Asaas. Será criptografada antes de salvar.'
            }
            error={apiKey.length > 0 && !apiKeyValid ? 'Mínimo 10 caracteres.' : undefined}
          >
            {({ inputId }) => (
              <Input
                id={inputId}
                type="password"
                autoComplete="new-password"
                placeholder={mode === 'edit' ? '••••••••' : 'sk_live_...'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Webhook secret"
            required={webhookSecretRequired}
            hint={
              mode === 'edit'
                ? 'Deixe vazio pra manter o atual. Preencha pra rotacionar.'
                : 'Token HMAC pra validar webhooks recebidos.'
            }
            error={webhookSecret.length > 0 && !secretValid ? 'Mínimo 8 caracteres.' : undefined}
          >
            {({ inputId }) => (
              <Input
                id={inputId}
                type="password"
                autoComplete="new-password"
                placeholder={mode === 'edit' ? '••••••••' : 'secret-token'}
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
            )}
          </Field>
        </div>
      </fieldset>

      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onCancel} disabled={isPending}>
          Cancelar
        </PillButton>
        <PillButton solid type="submit" disabled={!canSubmit}>
          {isPending ? 'Salvando…' : mode === 'create' ? 'Criar conta' : 'Salvar alterações'}
        </PillButton>
      </div>
    </form>
  );
}
